/**
 * AdminJobs — Phase 2.A operator view of background jobs.
 *
 * Lists every job with filters by status + kind. Click "Inspect" to
 * open a detail modal showing live polling, counters, error trace,
 * and (where safe) a cancel button.
 *
 * Lightweight on purpose — no charts, no aggregated metrics. The
 * goal is "operator can see what's running and intervene if needed".
 */
import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "../../components/ui-extras";
import { ArrowsClockwise, Stop, MagnifyingGlass } from "@phosphor-icons/react";
import { useJobPolling } from "../../lib/useJobPolling";
import { JobProgress } from "../../components/JobProgress";

const STATUS_VARIANT = {
  queued: "info",
  running: "info",
  retrying: "warning",
  completed: "success",
  failed: "error",
  cancelled: "warning",
};

const STATUSES = ["", "queued", "running", "retrying", "completed", "failed", "cancelled"];

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterKind, setFilterKind] = useState("");
  const [drill, setDrill] = useState(null); // { job_id }
  const [auto, setAuto] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterStatus) params.status = filterStatus;
      if (filterKind) params.kind = filterKind;
      const { data } = await api.get("/jobs", { params });
      setJobs(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load jobs");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus, filterKind]);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [auto, filterStatus, filterKind]);

  const cancel = async (jobId) => {
    if (!window.confirm(`Cancel job ${jobId}? In-flight work may not stop immediately.`)) return;
    try {
      await api.post(`/jobs/${jobId}/cancel`);
      toast.success("Cancel requested");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-jobs-page">
      <PageHeader
        title="Background Jobs"
        subtitle="Live worker activity. Auto-refreshes every 3s. Cancel is best-effort — in-flight work may not stop immediately."
        actions={
          <>
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} data-testid="jobs-auto-refresh"/>
              Auto-refresh
            </label>
            <button className="gf-btn gf-btn-outline" onClick={load} data-testid="refresh-jobs">
              <ArrowsClockwise size={14}/> Refresh
            </button>
          </>
        }
      />

      <div className="gf-card gf-card-body flex flex-wrap gap-3 items-end">
        <div>
          <div className="overline mb-1">STATUS</div>
          <select className="gf-input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-job-status">
            {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
          </select>
        </div>
        <div>
          <div className="overline mb-1">KIND</div>
          <input className="gf-input" placeholder="e.g. transaction_import" value={filterKind} onChange={(e) => setFilterKind(e.target.value)} data-testid="filter-job-kind"/>
        </div>
        <div className="text-xs text-zinc-500">Showing {jobs.length} job(s).</div>
      </div>

      <div className="gf-card">
        <div className="overflow-auto">
          <table className="gf-table" data-testid="jobs-table">
            <thead>
              <tr>
                <th>CREATED</th>
                <th>KIND</th>
                <th>STATUS</th>
                <th className="text-right">PROGRESS</th>
                <th>STEP</th>
                <th className="text-right">RETRY</th>
                <th>OWNER</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center text-zinc-400 py-8">Loading…</td></tr>}
              {!loading && jobs.length === 0 && (
                <tr><td colSpan={8} className="text-center text-zinc-400 py-8">No jobs match the current filters.</td></tr>
              )}
              {jobs.map((j) => {
                const pct = Math.max(0, Math.min(100, j.progress_percent || 0));
                const isRunning = ["queued", "running", "retrying"].includes(j.status);
                return (
                  <tr key={j.job_id} data-testid={`job-row-${j.job_id}`}>
                    <td className="font-mono text-xs">{j.created_at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="font-mono text-xs">{j.kind}</td>
                    <td><Badge variant={STATUS_VARIANT[j.status] || "info"}>{j.status}</Badge></td>
                    <td className="gf-num font-mono text-xs">{pct}%</td>
                    <td className="text-xs text-zinc-600 max-w-[200px] truncate" title={j.current_step || ""}>{j.current_step || "—"}</td>
                    <td className="gf-num">{j.retry_count || 0}</td>
                    <td className="text-xs text-zinc-600">{j.owner_user_id?.slice(-6) || "system"}</td>
                    <td className="text-right">
                      <button className="gf-btn gf-btn-ghost" onClick={() => setDrill({ job_id: j.job_id })} data-testid={`drill-job-${j.job_id}`}>
                        <MagnifyingGlass size={14}/>
                      </button>
                      {isRunning && (
                        <button className="gf-btn gf-btn-ghost text-red-700" onClick={() => cancel(j.job_id)} data-testid={`cancel-job-${j.job_id}`} title="Cancel this job">
                          <Stop size={14}/>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {drill && <JobDrillModal jobId={drill.job_id} onClose={() => setDrill(null)} onCancel={cancel}/>}
    </div>
  );
}

function JobDrillModal({ jobId, onClose, onCancel }) {
  const { job } = useJobPolling(jobId, { intervalMs: 2000 });
  if (!job) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white p-6 border border-zinc-200" onClick={(e) => e.stopPropagation()}>
          <div className="text-sm">Loading…</div>
        </div>
      </div>
    );
  }
  const isRunning = ["queued", "running", "retrying"].includes(job.status);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="job-drill-modal">
        <div className="gf-card-header">
          <h3>{job.kind}</h3>
          <div className="text-xs text-zinc-500 mt-1 font-mono">{job.job_id}</div>
        </div>
        <div className="overflow-auto flex-1 p-4 space-y-3">
          <JobProgress job={job}/>
          {job.error && (
            <div className="border border-red-200 bg-red-50 p-3 text-xs">
              <div className="text-red-900 font-bold mb-1">Error</div>
              <pre className="whitespace-pre-wrap text-red-900">{job.error}</pre>
            </div>
          )}
          {job.result && (
            <details className="text-xs">
              <summary className="cursor-pointer text-zinc-600">Result</summary>
              <pre className="mt-2 p-3 bg-zinc-50 border border-zinc-200 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(job.result, null, 2)}</pre>
            </details>
          )}
          <details className="text-xs">
            <summary className="cursor-pointer text-zinc-600">Raw job document</summary>
            <pre className="mt-2 p-3 bg-zinc-50 border border-zinc-200 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(job, null, 2)}</pre>
          </details>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
          {isRunning && (
            <button className="gf-btn gf-btn-outline text-red-700" onClick={() => onCancel(job.job_id)} data-testid="cancel-job-drill">
              <Stop size={14}/> Cancel
            </button>
          )}
          <button className="gf-btn gf-btn-outline" onClick={onClose} data-testid="close-job-drill-modal">Close</button>
        </div>
      </div>
    </div>
  );
}
